import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BadgeCheck,
  BriefcaseBusiness,
  Check,
  CircleAlert,
  Clock3,
  CreditCard,
  FileSignature,
  ShieldCheck,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  acceptProviderAgreement,
  getMyProviderOnboarding,
  submitProviderApplication,
} from "../../entities/provider/api";
import {
  createAgreementAcceptancePayload,
  PROVIDER_AGREEMENTS,
} from "../../entities/provider/agreements";
import { getApiErrorMessage } from "../../shared/api/errors";
import { showToast } from "../../shared/ui/toast";
import ProviderSettlementSection from "./components/ProviderSettlementSection";
import SettingsNav from "./components/SettingsNav";

const statusMeta = {
  DRAFT: { label: "资料准备中", tone: "muted" },
  PENDING_IDENTITY: { label: "待实名认证", tone: "pending" },
  PENDING_AGREEMENT: { label: "待签署协议", tone: "pending" },
  PENDING_SETTLEMENT: { label: "待绑定结算账户", tone: "pending" },
  PENDING_REVIEW: { label: "平台审核中", tone: "pending" },
  ACTIVE: { label: "已成为服务者", tone: "success" },
  REJECTED: { label: "申请未通过", tone: "error" },
  SUSPENDED: { label: "服务者身份已冻结", tone: "error" },
  CLOSED: { label: "服务者身份已关闭", tone: "muted" },
};

const nextActionStatusMeta = {
  START_IDENTITY_VERIFICATION: statusMeta.PENDING_IDENTITY,
  ACCEPT_PROVIDER_AGREEMENT: statusMeta.PENDING_AGREEMENT,
  BIND_SETTLEMENT_RECIPIENT: statusMeta.PENDING_SETTLEMENT,
  WAIT_FOR_REVIEW: statusMeta.PENDING_REVIEW,
};

const stepDefinitions = [
  {
    key: "identity",
    label: "实名认证",
    description: "确认申请人身份与年龄条件",
    icon: ShieldCheck,
    complete: (provider) => Boolean(provider.adult_verified),
  },
  {
    key: "agreement",
    label: "服务协议",
    description: "阅读并接受平台服务者协议",
    icon: FileSignature,
    complete: (provider) => provider.agreement_status === "ACCEPTED",
  },
  {
    key: "settlement",
    label: "结算账户",
    description: "绑定用于服务收入结算的账户",
    icon: CreditCard,
    complete: (provider) => provider.settlement_status === "READY",
  },
  {
    key: "review",
    label: "平台审核",
    description: "完成准入审核并激活服务者身份",
    icon: BadgeCheck,
    complete: (provider) => provider.status === "ACTIVE",
  },
];

function ProviderStatus({ provider }) {
  const status = String(provider.status || "").toUpperCase();
  const terminalStatus = ["ACTIVE", "REJECTED", "SUSPENDED", "CLOSED"].includes(status);
  const meta = (!terminalStatus && nextActionStatusMeta[provider.next_action])
    || statusMeta[status]
    || { label: "状态处理中", tone: "muted" };

  return (
    <>
      <div className="provider-status-header">
        <div>
          <span className="provider-status-eyebrow">当前申请状态</span>
          <strong>{meta.label}</strong>
          {provider.provider_no ? <small>服务者编号：{provider.provider_no}</small> : null}
        </div>
        <span className={`provider-status-badge ${meta.tone}`}>
          {meta.tone === "success" ? <BadgeCheck size={16} /> : <Clock3 size={16} />}
          {meta.label}
        </span>
      </div>

      <ol className="provider-onboarding-steps" aria-label="服务者申请进度">
        {stepDefinitions.map((step) => {
          const complete = step.complete(provider);
          const StepIcon = step.icon;
          return (
            <li
              className={`provider-onboarding-step${complete ? " complete" : ""}`}
              key={step.key}
            >
              <span className="provider-step-icon">
                {complete ? <Check size={17} strokeWidth={2.5} /> : <StepIcon size={18} />}
              </span>
              <span>
                <strong>{step.label}</strong>
                <small>{complete ? "已完成" : step.description}</small>
              </span>
            </li>
          );
        })}
      </ol>
    </>
  );
}

function NextAction({
  provider,
  isSubmitting,
  onOpenAgreements,
  onResubmit,
}) {
  const status = String(provider.status || "").toUpperCase();
  const nextAction = provider.next_action;

  if (status === "ACTIVE") {
    return (
      <div className="provider-next-action success">
        <BadgeCheck size={20} />
        <div>
          <strong>服务者身份已激活</strong>
          <span>你现在可以使用服务者相关功能。</span>
        </div>
      </div>
    );
  }

  if (status === "REJECTED") {
    return (
      <div className="provider-next-action error">
        <CircleAlert size={20} />
        <div>
          <strong>本次申请未通过</strong>
          <span>完善准入条件后，可以重新提交服务者申请。</span>
        </div>
        <button
          className="secondary-button"
          type="button"
          disabled={isSubmitting}
          onClick={onResubmit}
        >
          {isSubmitting ? "提交中……" : "重新提交"}
        </button>
      </div>
    );
  }

  if (nextAction === "START_IDENTITY_VERIFICATION") {
    return (
      <div className="provider-next-action">
        <ShieldCheck size={20} />
        <div>
          <strong>下一步：完成实名认证</strong>
          <span>通过实名认证后，才可继续服务者准入流程。</span>
        </div>
        <Link className="primary-button" to="/settings/security">前往认证</Link>
      </div>
    );
  }

  if (nextAction === "ACCEPT_PROVIDER_AGREEMENT") {
    return (
      <div className="provider-next-action">
        <FileSignature size={20} />
        <div>
          <strong>下一步：签署服务者协议</strong>
          <span>阅读并同意服务者协议与支付结算协议。</span>
        </div>
        <button className="primary-button" type="button" onClick={onOpenAgreements}>
          查看并签署
        </button>
      </div>
    );
  }

  if (nextAction === "BIND_SETTLEMENT_RECIPIENT") {
    return null;
  }

  if (nextAction === "WAIT_FOR_REVIEW" || status === "PENDING_REVIEW") {
    return (
      <div className="provider-next-action">
        <Clock3 size={20} />
        <div>
          <strong>申请正在审核</strong>
          <span>审核完成后，服务者状态会自动更新。</span>
        </div>
      </div>
    );
  }

  if (["SUSPENDED", "CLOSED"].includes(status)) {
    return (
      <div className="provider-next-action error">
        <CircleAlert size={20} />
        <div>
          <strong>{statusMeta[status].label}</strong>
          <span>当前状态无法重新提交申请，请联系平台处理。</span>
        </div>
      </div>
    );
  }

  return null;
}

function AgreementDialog({ isSubmitting, onClose, onSubmit }) {
  const [confirmedTypes, setConfirmedTypes] = useState([]);
  const allConfirmed = PROVIDER_AGREEMENTS.every((agreement) => (
    confirmedTypes.includes(agreement.type)
  ));

  function toggleAgreement(type) {
    setConfirmedTypes((current) => (
      current.includes(type)
        ? current.filter((item) => item !== type)
        : [...current, type]
    ));
  }

  return (
    <div
      className="provider-agreement-modal"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSubmitting) onClose();
      }}
    >
      <section
        className="provider-agreement-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="provider-agreement-title"
      >
        <header>
          <div>
            <span>服务者准入</span>
            <h3 id="provider-agreement-title">阅读并签署协议</h3>
          </div>
          <button
            type="button"
            aria-label="关闭协议"
            disabled={isSubmitting}
            onClick={onClose}
          >
            <X size={21} />
          </button>
        </header>

        <div className="provider-agreement-documents">
          {PROVIDER_AGREEMENTS.map((agreement) => (
            <article className="provider-agreement-document" key={agreement.type}>
              <div className="provider-agreement-document-heading">
                <div>
                  <h4>{agreement.title}</h4>
                  <span>版本 {agreement.version}</span>
                </div>
                <FileSignature size={20} />
              </div>
              {agreement.sections.map((section) => (
                <section key={section.title}>
                  <h5>{section.title}</h5>
                  <p>{section.content}</p>
                </section>
              ))}
              <label className="provider-agreement-confirmation">
                <input
                  type="checkbox"
                  checked={confirmedTypes.includes(agreement.type)}
                  disabled={isSubmitting}
                  onChange={() => toggleAgreement(agreement.type)}
                />
                <span>我已完整阅读并同意《{agreement.title}》</span>
              </label>
            </article>
          ))}
        </div>

        <footer>
          <p>签署后将记录协议版本、正文摘要与签署时间。</p>
          <button
            className="primary-button"
            type="button"
            disabled={!allConfirmed || isSubmitting}
            onClick={onSubmit}
          >
            {isSubmitting ? "正在签署……" : "同意并签署"}
          </button>
        </footer>
      </section>
    </div>
  );
}

export default function ProviderApplicationPage() {
  const queryClient = useQueryClient();
  const [agreementDialogOpen, setAgreementDialogOpen] = useState(false);
  const providerQuery = useQuery({
    queryKey: ["provider", "me"],
    queryFn: getMyProviderOnboarding,
  });
  const provider = providerQuery.data;
  const applicationMutation = useMutation({
    mutationFn: submitProviderApplication,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["provider", "me"] });
      showToast("服务者申请已提交");
    },
    onError: (error) => {
      showToast(getApiErrorMessage(error, "服务者申请提交失败"), "error");
    },
  });
  const agreementMutation = useMutation({
    mutationFn: async () => {
      const acceptances = [];
      for (const agreement of PROVIDER_AGREEMENTS) {
        const payload = await createAgreementAcceptancePayload(agreement);
        acceptances.push(await acceptProviderAgreement(payload));
      }
      return acceptances;
    },
    onSuccess: async () => {
      setAgreementDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["provider", "me"] });
      showToast("服务者协议签署成功");
    },
    onError: (error) => {
      showToast(getApiErrorMessage(error, "服务者协议签署失败"), "error");
    },
  });

  return (
    <section className="settings-page">
      <SettingsNav />
      <div className="settings-content">
        <div className="settings-panel">
          <h2>服务者申请</h2>
          <p className="settings-description">
            完成平台准入流程，成为可承接平面设计需求的认证服务者。
          </p>

          {providerQuery.isPending ? (
            <div className="provider-query-state">正在查询申请状态……</div>
          ) : providerQuery.isError ? (
            <div className="provider-query-state error">
              <CircleAlert size={20} />
              <div>
                <strong>申请状态加载失败</strong>
                <span>{getApiErrorMessage(providerQuery.error, "请稍后重新加载")}</span>
              </div>
              <button
                className="secondary-button"
                type="button"
                onClick={() => providerQuery.refetch()}
              >
                重新加载
              </button>
            </div>
          ) : provider ? (
            <div className="provider-application-content">
              <ProviderStatus provider={provider} />
              <NextAction
                provider={provider}
                isSubmitting={applicationMutation.isPending}
                onOpenAgreements={() => setAgreementDialogOpen(true)}
                onResubmit={() => applicationMutation.mutate()}
              />
              <ProviderSettlementSection provider={provider} />
            </div>
          ) : (
            <div className="provider-application-intro">
              <div className="provider-intro-icon"><BriefcaseBusiness size={26} /></div>
              <div>
                <h3>将设计能力转化为专业服务</h3>
                <p>
                  提交申请后，需要依次完成实名认证、服务协议、结算账户与平台审核。
                </p>
              </div>
              <ul className="provider-benefits">
                <li><Check size={16} />承接海报、宣传图等平面设计需求</li>
                <li><Check size={16} />使用平台订单与服务评价体系</li>
                <li><Check size={16} />通过平台流程保障交易与结算安全</li>
              </ul>
              <button
                className="primary-button"
                type="button"
                disabled={applicationMutation.isPending}
                onClick={() => applicationMutation.mutate()}
              >
                {applicationMutation.isPending ? "正在提交……" : "提交服务者申请"}
              </button>
              <small className="provider-application-note">
                提交即表示你愿意进入服务者准入流程，申请不会立即激活接单权限。
              </small>
            </div>
          )}
        </div>
      </div>
      {agreementDialogOpen ? (
        <AgreementDialog
          isSubmitting={agreementMutation.isPending}
          onClose={() => setAgreementDialogOpen(false)}
          onSubmit={() => agreementMutation.mutate()}
        />
      ) : null}
    </section>
  );
}
